import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { NgbActiveModal, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RegistrationService } from '../registration.service';
import { AccountCreationModalComponent } from '../account-creation-modal/account-creation-modal.component';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { Alert } from '@app/@core/common/common.entity';
import { AlertService } from '@app/@core/services/alert.service';
import { LocalService } from '@app/@core/services/local.service';
import { take, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-confirm-registration-modal',
  templateUrl: './confirm-registration-modal.component.html',
  styleUrls: ['./confirm-registration-modal.component.scss']
})
export class ConfirmRegistrationModalComponent implements OnInit, OnDestroy {
  @Input() formData: any;
  showSSN = false;
  displayedSSN = '';
  modalRef: NgbModalRef;
  isLoading: boolean = false;
  registeredData:any;
   private destroy$ = new Subject<void>();
  constructor(
    private readonly activeModal: NgbActiveModal,
    private readonly registrationService: RegistrationService,
    private readonly ngModal: NgbModal,
    private readonly router: Router,
    private readonly alertService: AlertService,
    private readonly sessionStorage: LocalService
  ) {}

  ngOnInit(): void {
    this.updateDisplayedSSN();
  }

  modalClose() {
    this.activeModal.close();
  }
 toggleSSN() {
    this.showSSN = !this.showSSN;
    this.updateDisplayedSSN();
  }
  updateDisplayedSSN() {
    const ssn = this.formData.ssn || this.formData.itin || '';
    const digits = ssn.replace(/\D/g, '');
    if (this.showSSN) {
      // Show full formatted SSN
      this.displayedSSN = ssn;
    } 
      // Mask all but last 4 digits
      else if (!this.showSSN  && digits.length === 9) {
        this.displayedSSN = `***-**-${digits.substring(5)}`;
      } else if (!this.showSSN && digits.length > 5) {
        this.displayedSSN = `***-**-${digits.substring(5)}`;
      } else if (!this.showSSN ) {
        this.displayedSSN = ssn.replace(/\d/g, '*');
      }
    
  }
  submitDetails() {
    this.isLoading = true;
    if(this.formData.itin && this.formData.itin.trim() !== ''){
      this.register();
    }else{
    const payload = {
      first_name: this.formData.firstName,
      last_name: this.formData.lastName,
      birth_date: this.formData.dob?.replace(/\//g, '-'),
      ssn_number: this.formData.ssn?.replace(/\D/g, '') || null
    };

    this.registrationService.checkPossibleDuplicate(payload)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.data && response.data.isDuplicate === true) {
            this.isLoading = false;
            this.activeModal.close({ duplicateAccount: true });
          } else if (response.data && response.data.isDuplicate === false) {
            this.register();
          } else {
            this.isLoading = false;
            this.alertService.error('Failed to verify registration. Please try again.');
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.error('Error checking duplicate person:', error);
          this.alertService.error('Failed to verify registration. Please try again.');
        }
      });
    }
  }
  register() {
    this.isLoading = true;
    const cleanedPhoneNumber = this.formData.phoneNumber.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');
    
    let payload = {
      first_name: this.formData.firstName,
      middle_name: this.formData.middleName,
      last_name: this.formData.lastName,
      suffix_id: this.formData.suffix,
      birth_date: this.formData.dob ,
      primary_email: this.formData.email,
      primary_phone: cleanedPhoneNumber,
      gender_id: this.formData.gender,
      race_id: this.formData.race,
      ethnicity_id: this.formData.ethnicity,
      recovery_email: this.formData.recovery_email,
      is_consent: this.formData.consent,
      is_channel: this.formData.terms
    };
    
    // Add either SSN or ITIN to the payload based on which one exists
    if (this.formData.ssn?.trim()) {
      const cleanedSSN = this.formData.ssn.replace(/\D/g, '');
      payload['ssn_number'] = cleanedSSN;
      payload['itin_number'] = null;
    } else if (this.formData.itin?.trim()) {
      const cleanedITIN = this.formData.itin.replace(/\D/g, '');
      payload['itin_number'] = cleanedITIN;
      payload['ssn_number'] = null;
    }

    this.registrationService.register(payload).pipe(take(1), takeUntil(this.destroy$)).subscribe({
      next: (response: any) => {
        
        // Handle successful registration
        if (response.data.success === true) {
          this.registeredData = response.data.data;
          this.emailSent();
          if (this.formData.recovery_email) {
            this.sendRecoveryEmailVerification();
          }
          this.isLoading = false;
          this.activeModal.close();
          this.accountCreated();
          
        } else {
          this.isLoading = false;
          this.alertService.error('Registration failed. Please try again.');         
        }
      },
      error: error => {
        this.isLoading = false;
        console.error('Error registering person:', error);
        this.alertService.error('Registration failed. Please try again.'); 
        // this.activeModal.close({ duplicateAccount: true });  
      }
    });
  }
 
  
  accountCreated() {
    const options: NgbModalOptions = {
      backdrop: 'static',
      centered: true,
      size: 'md'
    };
    this.modalRef = this.ngModal.open(AccountCreationModalComponent, options);
    this.modalRef.componentInstance.isSuccess = true;

    setTimeout(() => {
      // Clear session data and reload the page
      sessionStorage.clear();
      this.sessionStorage.clear();
      localStorage.clear();
      if (this.modalRef) {
        this.modalRef.close();
        this.router.navigate(['']);
      }
    }, 15000);
  }
  emailSent() {
    const cleanedPhoneNumber = this.formData.phoneNumber.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1');
    let request = {
      first_name: this.formData.firstName,
      last_name: this.formData.lastName,
      email: this.formData.email,
      primary_phone: cleanedPhoneNumber,
      front_end_domain: environment.frontEndDomain,
      recovery_email: this.formData.recovery_email,
      createdDate: this.registeredData.created_date,
    };
    this.registrationService.completeRegistration(request).subscribe({
      next: response => {
        if (response.status === true) {
          console.log('email sent');
        } else {
          console.log('error sending email');
        }
      },
      error: error => {
        console.error('Error registering person:', error);
      }
    });
  }
    sendRecoveryEmailVerification() {
    let request = {
      email: this.formData.recovery_email
    };
    
    this.registrationService.sendRecoveryEmailOtp(request).subscribe({
      next: response => {
        console.log('Recovery email verification sent successfully:', response);
        console.log('OTP email sent to recovery email address');
      },
      error: error => {
        console.error('Error sending recovery email verification:', error);

      }
    });
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
